/**
 * CanvasGrid - Visual grid overlay for the canvas builder
 * Shows grid lines and cell boundaries in edit mode
 */

import React, { memo, useMemo } from "react";
import { Box } from "@mui/joy";
import type { CanvasGridConfig } from "../../types";

interface CanvasGridProps {
  config: CanvasGridConfig;
  maxRow: number;
  visible: boolean;
}

/**
 * Visual grid overlay showing cell boundaries.
 * Only visible in edit mode.
 */
function CanvasGridComponent({ config, maxRow, visible }: CanvasGridProps) {
  const { columns, rowHeight, gap, minRows } = config;

  // Calculate total rows to display
  const totalRows = Math.max(maxRow + 2, minRows);

  // Generate row lines
  const rowLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i <= totalRows; i++) {
      lines.push(
        <Box
          key={`row-${i}`}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: i * (rowHeight + gap),
            height: gap > 0 ? gap : 1,
            bgcolor: "neutral.outlinedBorder",
            opacity: 0.3,
            pointerEvents: "none",
          }}
        />,
      );
    }
    return lines;
  }, [totalRows, rowHeight, gap]);

  // Generate column lines
  const columnLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i <= columns; i++) {
      lines.push(
        <Box
          key={`col-${i}`}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `calc(${(i / columns) * 100}% - ${gap / 2}px)`,
            width: gap > 0 ? gap : 1,
            bgcolor: "neutral.outlinedBorder",
            opacity: 0.3,
            pointerEvents: "none",
          }}
        />,
      );
    }
    return lines;
  }, [columns, gap]);

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        minHeight: totalRows * (rowHeight + gap),
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {rowLines}
      {columnLines}
    </Box>
  );
}

export const CanvasGrid = memo(CanvasGridComponent);
export default CanvasGrid;
